/**
 * The main loop's pass over the bubbles (spec 02 §4 Float, Bunching, Wrap), ported from rebb64
 * enemy_ai_update $0CF2 - which runs the software entities, not the enemies: a shot takes its step;
 * a floating bubble is burst by a player touching it (L0D02), or moves 2 px - away from a neighbour
 * (bunching, L0D86) or with the wind map (L0E00) - and wraps through the holes (L0E23).
 */

import { windAt, type Wind } from '../playfield';
import { rngOf } from '../random';
import { GONE, POPPING, TRAPPED, isBubbleCode } from './codes';
import { shotPass } from './shot';
import { bySlot, colOf, setCode } from './slots';
import { specialSteps } from './specials';
import {
  BUNCH_CHANCE, BUNCH_RANGE, COL_MAX, COL_MIN, FLOAT_PX, ROW_MAX, ROW_MIN, TOUCH_RANGE, TOUCH_X_BIAS,
  WRAP_AT_BOTTOM, WRAP_TO_BOTTOM,
} from './tuning';
import type { Bubble, World } from '../types';

/** The C64's directions: 0 up, 1 right, 2 down, 3 left (the wind map's two bits). */
const UP = 0;
const RIGHT = 1;
const DOWN = 2;
const LEFT = 3;
const WIND: Record<Wind, number> = { up: UP, right: RIGHT, down: DOWN, left: LEFT };

/** $0CF2: every bubble, slot 17 first, with the drops, bolts and foods in their slots' turn (M2). */
export function moveBubbles(world: World): void {
  const specials = specialSteps(world);
  let k = 0;
  for (const b of bySlot(world)) {
    for (; k < specials.length && (specials[k]?.slot ?? -1) > b.motion.slot; k++) specials[k]?.step();
    const m = b.motion;
    if (!isBubbleCode(m.code)) continue;
    if (m.steps !== 0) shotPass(world, b);
    else if (!touchPop(world, b)) float(world, b);
  }
  for (; k < specials.length; k++) specials[k]?.step();
}

/**
 * L0D02: a player (2 first) not dying and within |(Px + 2) - Bx| < 16, |Py - By| < 16 bursts the
 * bubble, credited to them; it spreads its chain on the next pass.
 */
function touchPop(world: World, b: Bubble): boolean {
  for (const id of [1, 0] as const) {
    const p = world.players[id];
    if (p.state !== 'play') continue;
    if (Math.abs(((p.x + TOUCH_X_BIAS) & 0xff) - b.x) >= TOUCH_RANGE || Math.abs(p.y - b.y) >= TOUCH_RANGE) continue;
    b.motion.credit = id;
    b.motion.saved = b.motion.code;
    setCode(b, POPPING);
    return true;
  }
  return false;
}

/**
 * L0D86: on 234 of 256 passes the bubble looks for another floating bubble within 16 px; finding one,
 * it moves 2 px a random way instead of following the wind.
 */
function float(world: World, b: Bubble): void {
  const rng = rngOf(world);
  if (rng.int(256) < BUNCH_CHANCE) {
    for (const o of bySlot(world)) {
      if (o === b || o.motion.steps !== 0 || !isBubbleCode(o.motion.code)) continue;
      if (Math.abs(b.x - o.x) >= BUNCH_RANGE || Math.abs(b.y - o.y) >= BUNCH_RANGE) continue;
      move(b, bunchDirection(world, b));
      return;
    }
  }
  move(b, windDirection(world, b));
}

/**
 * L0DC4: vertical when rand < the level index - the compare's operand at $0DC7 is overwritten with the
 * level number (D_F23C), so level 1 separates only sideways. Rows < 4 go down, >= $1C up; columns < 2
 * go right, >= $1C left; else a random way.
 */
function bunchDirection(world: World, b: Bubble): number {
  const rng = rngOf(world);
  if (rng.int(256) < world.levelIndex) {
    if (b.motion.row < ROW_MIN) return DOWN;
    if (b.motion.row >= ROW_MAX) return UP;
    return rng.int(256) & 2;
  }
  const col = colOf(b);
  if (col < COL_MIN) return RIGHT;
  if (col >= COL_MAX) return LEFT;
  return (rng.int(256) | 1) & 3;
}

/**
 * L0E00: the wind map cell one row below map row (row - 4) and one column right of the bubble's
 * column, with the row held to 4..$1C: tile (row - 3, col + 1), the cell at the bubble's centre.
 */
function windDirection(world: World, b: Bubble): number {
  const row = Math.max(ROW_MIN, Math.min(ROW_MAX, b.motion.row));
  return WIND[windAt(world.playfield, colOf(b) + 1, row - 3)];
}

/**
 * L0E23: 2 px. Vertically the sub-row steps 2 px; the row changes as the C64 counts it (up: when the
 * sub-row comes back to 0; down: when it leaves 0). Past row 0 the row becomes $1A, and row $1D
 * becomes 0: a bubble without a monster (type < $18) is deleted there, one with a monster wraps.
 * Y wraps at 256 on its own, so a wrapped trapped bubble's Y and row part (README.md).
 */
function move(b: Bubble, dir: number): void {
  const m = b.motion;
  if (dir === RIGHT) {
    b.x = (b.x + FLOAT_PX) & 0xff;
  } else if (dir === LEFT) {
    b.x = (b.x - FLOAT_PX) & 0xff;
  } else if (dir === UP) {
    m.subY = (m.subY - FLOAT_PX) & 7;
    b.y = (b.y - FLOAT_PX) & 0xff;
    if (m.subY === 0 && --m.row < 0) {
      m.row = WRAP_TO_BOTTOM;
      if (m.code < TRAPPED) setCode(b, GONE);
    }
  } else {
    m.subY += FLOAT_PX;
    b.y = (b.y + FLOAT_PX) & 0xff;
    if (m.subY === 8) {
      m.subY = 0;
    } else if (m.subY === FLOAT_PX && ++m.row === WRAP_AT_BOTTOM) {
      m.row = 0;
      if (m.code < TRAPPED) setCode(b, GONE);
    }
  }
}
